import { supabase } from "../../supabaseClient";
import { notifyAdmins } from "./realtime";

async function addEarning(userId, earning) {
  const { error } = await supabase.from('earnings').insert({ user_id: userId, ...earning });
  if (error) throw error;
}

async function getEarnings(userId, startDate = null, endDate = null) {
  try {
    let query = supabase.from('earnings').select('*').eq('user_id', userId).order('date', { ascending: false });

    if (startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      query = query.gte('date', start.toISOString()).lte('date', end.toISOString());
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Erro ao buscar ganhos:", error);
    throw error;
  }
}

async function deleteEarning(userId, earningId) {
  try {
    const { error } = await supabase.from('earnings').delete().eq('id', earningId).eq('user_id', userId);
    if (error) throw error;
  } catch (error) {
    console.error("Erro ao deletar ganho:", error);
    throw error;
  }
}

async function addMaintenance(userId, maintenance) {
  const { error } = await supabase.from('maintenances').insert({ user_id: userId, ...maintenance });
  if (error) throw error;
}

async function getMaintenances(userId) {
  const { data, error } = await supabase.from('maintenances').select('*').eq('user_id', userId).order('date', { ascending: false });
  if (error) throw error;
  return data;
}

async function registerBike(userId, bikeData) {
  const { error } = await supabase.from('bike_profiles').upsert({ user_id: userId, ...bikeData }, { onConflict: 'user_id' });
  if (error) throw error;
}

async function getBikeData(userId) {
  const { data, error } = await supabase.from('bike_profiles').select('*').eq('user_id', userId).maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  return data || null;
}

const obterDadosMoto = getBikeData;

async function addFueling(userId, fuelingData) {
  try {
    const { data, error } = await supabase.from('fuelings').insert({ user_id: userId, ...fuelingData }).select('id').single();
    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error("Erro ao adicionar abastecimento:", error);
    throw error;
  }
}

async function getFuelings(userId, filterDate = null) {
  try {
    let query = supabase.from('fuelings').select('*').eq('user_id', userId).order('data', { ascending: false });

    if (filterDate) {
      const startOfDay = new Date(filterDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(filterDate);
      endOfDay.setHours(23, 59, 59, 999);

      query = query.gte('data', startOfDay.toISOString()).lte('data', endOfDay.toISOString());
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Erro ao buscar abastecimentos:", error);
    throw error;
  }
}

async function getLatestFueling(userId) {
  const { data, error } = await supabase.from('fuelings').select('*').eq('user_id', userId).order('data', { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  return data;
}

async function saveUserConfig(userId, configData) {
    const { error } = await supabase.from('user_configs').upsert({ user_id: userId, type: 'user', ...configData }, { onConflict: 'user_id,type' });
    if (error) throw error;

    const profileUpdates = {};
    if (configData.name) profileUpdates.name = configData.name;
    if (configData.cedula) profileUpdates.cedula = configData.cedula;
    
    if (Object.keys(profileUpdates).length > 0) {
        const { error: profileError } = await supabase.from('profiles').update(profileUpdates).eq('id', userId);
        if (profileError) console.error("Error updating profile from user config:", profileError);
    }
}

async function saveEarningsConfig(userId, configData) {
  const { error } = await supabase.from('user_configs').upsert({ user_id: userId, type: 'earnings', ...configData }, { onConflict: 'user_id,type' });
  if (error) throw error;
}

async function saveAppConfig(userId, configData) {
  const { error } = await supabase.from('user_configs').upsert({ user_id: userId, type: 'app', ...configData }, { onConflict: 'user_id,type' });
  if (error) throw error;
}

async function getUserConfig(userId) {
  const { data, error } = await supabase.from('user_configs').select('*').eq('user_id', userId).eq('type', 'user').maybeSingle();
  if (error) throw error;
  return data || null;
}

async function getEarningsConfig(userId) {
  const { data, error } = await supabase.from('user_configs').select('*').eq('user_id', userId).eq('type', 'earnings').maybeSingle();
  if (error) throw error;
  return data || null;
}

async function getAppConfig(userId) {
  const { data, error } = await supabase.from('user_configs').select('*').eq('user_id', userId).eq('type', 'app').maybeSingle();
  if (error) throw error;
  return data || null;
}

async function setDailyGoal(userId, goalValue) {
  await saveEarningsConfig(userId, { daily_goal: goalValue });
}

async function getDriverWallet(userId) {
  if (!userId) return { balance: 0.00, currency: 'USD', transactions: [] };
  try {
    // 1. Intentar vía RPC atómico (Security Definer)
    try {
      const { data: rpcData, error: rpcErr } = await supabase.rpc('get_wallet_summary', {
        p_driver_uid: userId
      });
      if (!rpcErr && rpcData) {
        return {
          balance: parseFloat(rpcData.balance || 0),
          currency: rpcData.currency || 'USD',
          transactions: rpcData.transactions || []
        };
      }
    } catch (_) {}

    // 2. Fallback: Consulta directa a tablas
    const { data: walletData, error: walletError } = await supabase
      .from('wallet')
      .select('id, balance, currency')
      .eq('driver_uid', userId)
      .maybeSingle();

    if (!walletError && walletData) {
      const txs = await getWalletTransactions(userId);
      return { 
        balance: parseFloat(walletData.balance || 0), 
        currency: walletData.currency || 'USD',
        transactions: txs 
      };
    }
    return { balance: 0.00, currency: 'USD', transactions: [] };
  } catch (err) {
    console.warn('Error in getDriverWallet:', err);
    return { balance: 0.00, currency: 'USD', transactions: [] };
  }
}

async function getWalletTransactions(userId) {
  if (!userId) return [];
  try {
    const { data: walletData, error: walletError } = await supabase
      .from('wallet')
      .select('id')
      .eq('driver_uid', userId)
      .maybeSingle();
    
    if (walletError || !walletData) return [];

    const { data, error } = await supabase
      .from('wallet_transactions')
      .select('*')
      .eq('wallet_id', walletData.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Warning fetching wallet transactions:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('Error in getWalletTransactions:', err);
    return [];
  }
}

async function getMinWalletBalance() {
  try {
    const { data, error } = await supabase
      .from('platform_config')
      .select('value')
      .eq('key', 'min_wallet_balance')
      .maybeSingle();
    
    if (!error && data?.value) return parseFloat(data.value);
    return 0.00;
  } catch {
    return 0.00;
  }
}

async function rechargeDriverWallet(driverUid, amount, metadata = {}) {
  try {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) throw new Error('Monto de recarga inválido');

    // 1. Intentar vía RPC atómico (Security Definer)
    try {
      const { data: rpcRes, error: rpcErr } = await supabase.rpc('recharge_driver_wallet', {
        p_driver_uid: driverUid,
        p_amount: numAmount,
        p_method: metadata.method || metadata.payment_method || 'Manual',
        p_reference: metadata.reference || null,
        p_notes: metadata.notes || null
      });

      if (!rpcErr && rpcRes && rpcRes.success) {
        return { success: true, balance: rpcRes.balance };
      }
    } catch (_) {
      // Fallback a tablas directas
    }

    // 2. Fallback: Obtener o crear wallet para el driver
    let { data: walletData, error: walletError } = await supabase
      .from('wallet')
      .select('id, balance')
      .eq('driver_uid', driverUid)
      .maybeSingle();

    if (walletError && walletError.code !== 'PGRST116') throw walletError;

    if (!walletData) {
      const { data: newWallet, error: createError } = await supabase
        .from('wallet')
        .insert({ driver_uid: driverUid, balance: numAmount })
        .select()
        .single();
      if (createError) throw createError;
      walletData = newWallet;
    } else {
      const newBalance = (parseFloat(walletData.balance) || 0) + numAmount;
      const { data: updatedWallet, error: updateError } = await supabase
        .from('wallet')
        .update({ balance: newBalance, updated_at: new Date().toISOString() })
        .eq('id', walletData.id)
        .select()
        .maybeSingle();
      if (updateError) console.warn('Warning on wallet table update:', updateError);
      walletData = updatedWallet || { ...walletData, balance: newBalance };
    }

    // 3. Registrar transacción en wallet_transactions (compatible con notes y description)
    const noteText = metadata.reference 
      ? `Recarga aprobada Ref: ${metadata.reference} (${metadata.method || metadata.payment_method || 'Manual'})` 
      : (metadata.notes || 'Recarga de saldo por administración');

    try {
      await supabase.from('wallet_transactions').insert({
        wallet_id: walletData.id,
        amount: numAmount,
        type: 'recharge',
        notes: noteText,
        created_at: new Date().toISOString(),
      });
    } catch (_) {}

    // 4. Notificar al conductor (RPC definer: destinatario es tercero)
    try {
      await supabase.rpc('create_notification', {
        p_user_id: driverUid,
        p_type: 'payment',
        p_title: '¡Recarga de Saldo Exitosa!',
        p_body: `Se han acreditado $${numAmount.toFixed(2)} a tu billetera. Tu saldo disponible es de $${parseFloat(walletData.balance).toFixed(2)}.`,
        p_data: { amount: numAmount, new_balance: walletData.balance },
      });
    } catch (_) {}

    return { success: true, balance: walletData.balance };
  } catch (error) {
    console.error('Error in rechargeDriverWallet:', error);
    throw error;
  }
}

async function deductServiceCommission(serviceId, driverUid, commissionAmount) {
  try {
    // RPC SECURITY DEFINER (v9h): monto derivado del servicio en BD (no
    // parámetro libre), idempotente, auditable. Reemplaza las escrituras
    // directas a wallet/wallet_transactions que RLS bloquea para conductores.
    const { data, error } = await supabase.rpc('deduct_service_commission', {
      p_service_id: String(serviceId),
    });
    if (error) throw error;
    if (data && data.success === false) {
      console.warn('Comision no deducida:', data.error);
    }
    return data;
  } catch (error) {
    console.error('Error deducting service commission:', error);
    return { success: false, error: error.message };
  }
}

async function requestDriverPayout(driverUid, amount, payoutDetails = {}) {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount) || numAmount <= 0) throw new Error('Monto de retiro inválido');

  const method = (payoutDetails.method || 'pago_movil')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '_');
  const isTransfer = method === 'transferencia';

  const { data, error } = await supabase.rpc('request_driver_payout', {
    p_amount: numAmount,
    p_method: method,
    p_destination: {
      bank: isTransfer ? '' : (payoutDetails.account || ''),
      account: isTransfer ? (payoutDetails.account || '') : '',
      phone: payoutDetails.phone || '',
      holder: payoutDetails.holder || '',
      notes: payoutDetails.notes || '',
    },
  });

  if (error) throw new Error(error.message || 'No se pudo procesar la solicitud de retiro');
  if (!data || data.success === false) {
    throw new Error(data?.error || 'No se pudo procesar la solicitud de retiro');
  }

  return {
    success: true,
    request_id: data.request_id,
    new_balance: data.new_balance,
    transaction: { id: data.transaction_id },
  };
}

async function submitDriverRecharge(driverUid, amount, rechargeDetails = {}) {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount) || numAmount <= 0) throw new Error('Monto de recarga inválido');

  let { data: walletData, error: walletError } = await supabase
    .from('wallet')
    .select('id')
    .eq('driver_uid', driverUid)
    .maybeSingle();

  if (!walletData) {
    const { data: newWallet } = await supabase
      .from('wallet')
      .insert({ driver_uid: driverUid, balance: 0 })
      .select()
      .single();
    walletData = newWallet;
  }

  const methodDesc = `${rechargeDetails.method || 'Pago Móvil'} - Ref: ${rechargeDetails.reference || 'S/R'}`;

  // 1. Registrar transacción en wallet_transactions como pendiente
  const { data: tx, error: txError } = await supabase
    .from('wallet_transactions')
    .insert({
      wallet_id: walletData.id,
      amount: numAmount,
      type: 'recharge',
      notes: `Reporte de Recarga: ${methodDesc}`,
      created_at: new Date().toISOString()
    })
    .select()
    .single();

  if (txError) console.warn('Warning on wallet_transaction insert:', txError);

  // 2. Registrar en payment_requests para que el Admin verifique el comprobante
  try {
    await supabase.from('payment_requests').insert({
      requester_id: driverUid,
      amount: numAmount,
      payment_method: (rechargeDetails.method || 'pago_movil').toLowerCase().replace(/\s+/g, '_'),
      reference: rechargeDetails.reference || `REC-${Date.now().toString().slice(-6)}`,
      notes: `Reporte de Recarga de Conductor: ${rechargeDetails.notes || ''} (${methodDesc})`,
      status: 'pending'
    });

    await notifyAdmins({
      type: 'recharge_request',
      title: '💰 Reporte de Recarga de Conductor',
      body: `Recarga por $${numAmount.toFixed(2)} (${methodDesc}) pendiente de conciliación.`,
      link: '/admin/pagos'
    });
  } catch (err) {
    console.warn('Warning creating payment_request for recharge:', err);
  }

  return { success: true, transaction: tx };
}

export {
  addEarning,
  deleteEarning,
  getEarnings,
  registerBike,
  getBikeData,
  obterDadosMoto,
  addFueling,
  getFuelings,
  getLatestFueling,
  addMaintenance,
  getMaintenances,
  saveUserConfig,
  saveEarningsConfig,
  saveAppConfig,
  getUserConfig,
  getEarningsConfig,
  getAppConfig,
  setDailyGoal,
  getDriverWallet,
  getWalletTransactions,
  getMinWalletBalance,
  rechargeDriverWallet,
  deductServiceCommission,
  requestDriverPayout,
  submitDriverRecharge
};
